import React, { useEffect, useMemo, useState } from "react";

import { Modal } from "@material-ui/core";
import { AnnouncementNotification } from "@remar/shared/dist/components/Notifications";
import useSearchParams from "@remar/shared/dist/hooks/useSearchParams";
import { NotificationTypeId } from "@remar/shared/dist/models";
import { LocationEnum } from "@remar/shared/dist/services/subjectCategories/modal";
import useToastNotifications from "@remar/shared/dist/services/useToastNotifications";
import { getCurrentSubscription } from "@remar/shared/dist/utils/subscriptionUtils";
import { differenceInCalendarDays, isToday } from "date-fns";
import { useUpgradeForm } from "hooks/useUpgradeForm";
import { useHistory } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "store";
import {
	applyValidatedCoupon,
	getFullState,
	getUserData,
	getUserStudyGuide,
	setPaymentDiscount,
	setSelectSubModal,
	setUpgradeModal
} from "store/features/Auth/authSlice";
import { getFullState as getFullBillingState, getPaymentDetails } from "store/features/Billing/BillingSlice";
import {
	getSubscriptionInfo,
	getUserInfo,
	selectRecommendedMaterial,
	selectSubscriptionInfo,
	setRecommendedMaterial
} from "store/features/MyAccount/myAccountSlice";
import {
	markAsRead,
	selectAnnouncementNotifications,
	setBannerActionLoading,
	setBannerData
} from "store/features/notifications/notifications.slice";

import { routes } from "core/constants";

import StudentForm from "modules/Course/StudentForm";

import ChangeSubscription from "modules/MyAccount/ChangeSubscription";

import UpgradeSubscription from "modules/MyAccount/UpgradeSubscription";

import AddCardDetails from "./AddCardDetails";
import CATRecommendedMaterials from "./CATRecommendedMaterials";

import { GLOBAL_CONSTANTS } from "../../../../constants";

import BaseLayout from "../../BaseLayout";

const DAYS_BEFORE_EXPIRY = -10;
const StudentModal = ({ modal, closeModal }) => (
	<Modal
		style={{
			alignItems: "center",
			justifyContent: "center",
			display: "flex"
		}}
		disableEnforceFocus
		disableAutoFocus
		open={!!modal}
	>
		<StudentForm closeModal={closeModal} />
	</Modal>
);
const GlobalWrapper = ({ children }: { children: React.ReactNode }) => {
	const [showAddCardDetailsModal, setShowAddCardDetailsModal] = useState(false);
	const { paymentMethods } = useAppSelector(getFullBillingState);
	const { pushToastNotification, dismissToastNotification } = useToastNotifications();
	const announcementNotifications = useAppSelector(selectAnnouncementNotifications);
	const dispatch = useAppDispatch();
	const history = useHistory();
	const params = useSearchParams();
	const shouldOpenUpgradeModal = params.get("upgrade");
	const showSchoolModal = GLOBAL_CONSTANTS.TOGGLE_SCHOOL_MODAL;

	const {
		user,
		paymentDiscount,
		isLoggedIn,
		inactiveSubscription: { isRenewEnabled },
		upgradeModal,
		selectSubModal,
		selectRenewalSubscription,
		qbAvailableOfferings,
		studyGuide,
		introVideo
	} = useAppSelector(getFullState);
	const showRecommendedMaterials = useAppSelector(selectRecommendedMaterial);
	const subscriptionInfo = useAppSelector(selectSubscriptionInfo);

	const [studentModal, setStudentModal] = useState(
		user && showSchoolModal && (!user.schoolId || (!user.hasTakenIntro && introVideo))
	);

	const isTrial = useMemo(() => getCurrentSubscription(user?.subscriptions).type?.isTrial, [user]);

	const onUpgradeSubmit = () => {
		dispatch(setUpgradeModal(false));
		dispatch(setSelectSubModal(false));
		dispatch(getUserData());
		dispatch(getUserInfo());
		dispatch(setPaymentDiscount(null));
		dispatch(getSubscriptionInfo());
		if (!!qbAvailableOfferings) {
			dispatch(setBannerData({ showBanner: false }));
		}
		history.push(`${routes.course.getPath()}?refetch=true`);
	};

	const { closeModal: closeUpgradeModel, ...upgradeFormProps } = useUpgradeForm({
		onSubmit: onUpgradeSubmit,
		isRenewSubscription: isRenewEnabled
	});

	useEffect(() => {
		if (announcementNotifications.items.length) {
			pushToastNotification(
				<AnnouncementNotification
					applyCoupon={c => {
						dispatch(applyValidatedCoupon(c));
						dispatch(markAsRead({ id: c.dismissId, notificationType: NotificationTypeId.AdminGeneratedAnnouncement }));
					}}
					dismiss={id => {
						dispatch(markAsRead({ id, notificationType: NotificationTypeId.AdminGeneratedAnnouncement }));
					}}
					announcements={announcementNotifications.items}
				/>,
				{
					toastId: "announcement",
					autoClose: false,
					closeOnClick: false
				}
			);
		} else if (announcementNotifications.items) {
			dismissToastNotification("announcement");
		}
	}, [announcementNotifications.items, dismissToastNotification, dispatch, pushToastNotification]);

	useEffect(() => {
		if (isLoggedIn && !paymentMethods) dispatch(getPaymentDetails());
	}, [dispatch, isLoggedIn, paymentMethods]);

	useEffect(() => {
		if (!paymentMethods) {
			const isInstitutionalStudent =
				!!user?.allowedLocations?.length &&
				user?.allowedLocations.some(l => l.id !== LocationEnum.Main && l.id !== LocationEnum.Common);
			const daysToExpire = differenceInCalendarDays(
				new Date(),
				new Date(subscriptionInfo?.subscription?.expiresOn || "")
			);

			setShowAddCardDetailsModal(isInstitutionalStudent && daysToExpire < 0 && DAYS_BEFORE_EXPIRY <= daysToExpire);
		}
	}, [user, subscriptionInfo, paymentMethods]);

	useEffect(() => {
		if (user && showSchoolModal) {
			setStudentModal(!user.schoolId || !!paymentDiscount || (!user.hasTakenIntro && introVideo));
		}
	}, [showSchoolModal, user, paymentDiscount, introVideo]);

	useEffect(() => {
		if (shouldOpenUpgradeModal === "true" && isTrial) {
			dispatch(setUpgradeModal(true));
		}
	}, [shouldOpenUpgradeModal, isTrial, dispatch]);

	// fetch user recommended materials
	useEffect(() => {
		if (isLoggedIn) {
			dispatch(
				getUserStudyGuide({
					cb: ({ studyGuide }) => {
						if (studyGuide) {
							dispatch(
								setBannerData({
									showBanner: true,
									bannerMessage:
										"Your scored low in your last CAT exam. Please View our Recommended Material for you to do better with Retake.",
									isTrial: false,
									isDismissible: true,
									takeOver: false,
									action: () => {
										dispatch(setRecommendedMaterial(true));
										dispatch(setBannerActionLoading(false));
									}
								})
							);
						}
					}
				})
			);
		}
	}, [dispatch, isLoggedIn]);

	const closeModal = () => setStudentModal(false);
	if (isLoggedIn) {
		return (
			<BaseLayout>
				{children}
				<StudentModal modal={studentModal} closeModal={closeModal} />
				<Modal open={upgradeModal! || selectSubModal!} onClose={closeUpgradeModel}>
					<UpgradeSubscription
						{...upgradeFormProps}
						selectRenewalSubscription={selectRenewalSubscription}
						closeModal={closeUpgradeModel}
					/>
				</Modal>
				<ChangeSubscription
					onCloseEnd={() => dispatch(setBannerActionLoading(false))}
					closeSideEffect={d => {
						if (isToday(d!)) {
							dispatch(
								setBannerData({
									showBanner: false,
									expiresIn: null,
									isTrial: false,
									isActionLoading: false
								})
							);
						}
					}}
				/>
				{showRecommendedMaterials && (
					<CATRecommendedMaterials onClose={() => dispatch(setRecommendedMaterial(false))} studyGuide={studyGuide} />
				)}
				{showAddCardDetailsModal && (
					<AddCardDetails onClose={() => setShowAddCardDetailsModal(false)} subscriptionInfo={subscriptionInfo} />
				)}
			</BaseLayout>
		);
	}
	return (
		<>
			{children}
			<StudentModal modal={studentModal} closeModal={closeModal} />
		</>
	);
};

export default GlobalWrapper;
